import React, { FC, useEffect, useMemo, useState } from 'react'
import './avatar.css'

// TYPES
import type { UserResource } from '@clerk/types'

// UTILITIES
import { flatten } from '@utils/string-helper'
import { isNonEmptyString, isObject, isString } from '@utils/type-checkers'
import { injectVars } from '@utils/temp-helpers'


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    slotName?: string
    user?: Partial<UserResource> | User | null
    iconColor?: string
    icon?: React.ReactNode
}

/** Base64 string of a 1x1 transparent png */
const transparentPng = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNwL/BhAAADCQEEpwQ2bwAAAABJRU5ErkJggg==`

const Avatar: FC<Props> = (props) => {
    const { className: classProps, style: styleProps, children, user = {}, slotName, iconColor, icon: iconElm, ...divProps } = props
    const className = flatten("avatar", 'select-none', classProps)
    const style = injectVars({ "--_avatar-icon-color": iconColor }, styleProps)

    const { firstName, lastName, imageUrl } = useMemo(() => {
        let firstName = '', lastName = '', imageUrl = transparentPng

        if (isUser(user)) {
            firstName = user.givenName || slotName || ''
            lastName = user.familyName || ''
            imageUrl = user.imageUrl || ''
        } else if (isUserResource(user)) {
            firstName = user.firstName || slotName || ''
            lastName = user.lastName || ''
            imageUrl = !!user.imageUrl && user.hasImage ? (user.imageUrl || '') : ''
        } else if (isNonUserParticipant(user)) {
            let name = user.givenName
            if (name === '' && slotName) name = slotName

            if (name) {
                const _givenName = name.split(" ");
                if (_givenName.length > 1) {
                    firstName = _givenName[0]
                    lastName = _givenName[1]
                } else firstName = name || ''
            }
        } else {
            let name = ''
            if (slotName) name = slotName

            if (name) {
                const _givenName = name.split(" ");
                if (_givenName.length > 1) {
                    firstName = _givenName[0]
                    lastName = _givenName[1]
                } else firstName = name || ''
            }
        }

        return ({ firstName, lastName, imageUrl })
    }, [user, slotName])
    const validSrc = useImageValidator(imageUrl)
    const icon = (iconElm == null || isString(iconElm))
        ? <p><strong>{iconElm ?? ((firstName == null || firstName == 'undefined' ? '' : `${(firstName.charAt(0) ?? '').toUpperCase()}${(lastName?.charAt(0) ?? '').toUpperCase()}`).trim() || 'U')}</strong></p>
        : iconElm

    return (
        <div className={className} {...divProps} style={style}>
            <div className={flatten("content icon", { visible: !validSrc })} style={{ backgroundColor: iconColor }}>{icon}</div>
            <div className={flatten("content image", { visible: !!validSrc })} style={{ backgroundImage: `url("${imageUrl}")` }} />
            {children}
        </div>
    )
}

// TODO: Move to schema/parsers if applicable after Zod integration
export const isUser = (value: unknown): value is User => isObject<User>(value) && isNonEmptyString(value.id) && isString(value.givenName) && isString(value.familyName) && isString(value.imageUrl)
export const isUserResource = (value: unknown): value is UserResource => (
    isObject<UserResource>(value)
    && isNonEmptyString(value.id)
    && isString(value.firstName)
    && isString(value.lastName)
    && isString(value.imageUrl)
)
const isNonUserParticipant = (value: unknown): value is User => isObject<User>(value) && isString(value.givenName)

const useImageValidator = (imageUrl: string) => {
    const [validSrc, setValidSrc] = useState(imageUrl !== '' && imageUrl !== transparentPng)

    useEffect(() => {
        const xhr = new XMLHttpRequest();
        (async () => {
            try {
                const exists = await loadImg(imageUrl)
                xhr.onload = () => {
                    const reader = new FileReader()
                    reader.onloadend = async () => {
                        const isDefault = reader.result === transparentPng
                        setValidSrc(exists && !isDefault)
                    }
                    reader.readAsDataURL(xhr.response)
                }
                xhr.open('GET', imageUrl)
                xhr.responseType = 'blob'
                xhr.send()
            } catch (error) {
                console.log(error)
                setValidSrc(false)
            }
        })()
        return () => { xhr.abort() }
    }, [imageUrl])

    return validSrc && imageUrl !== transparentPng && imageUrl !== ''
}

const loadImg = async (url: string) => await new Promise<boolean>((resolve, _) => {
    var img = new Image()
    img.src = url
    img.onload = () => { resolve(true) }
    img.onerror = () => { resolve(false) }
})

export default Avatar
